<!--
  Note: it's disabled everywhere as of April 2026, so no sense to update this components tree
-->
<template>
  <v-container fluid class="work-packages-container py-8">
    <!-- Delete dialog -->
    <alert-dialog
      :is-open="isDeleteDialogOpen"
      :ok-btn-text="$t('actions.delete')"
      @onOk="runDeleteWorkpackage"
      @onCancel="closeDeleteDialog"
    >
      <template v-slot:header>{{ $t('dialogs.deleteWorkpackage.header') }}</template>
      <template v-slot:body>
        {{ $t('dialogs.deleteWorkpackage.body') }}
        <strong>{{ workpackageToDelete ? workpackageToDelete.description : '' }}</strong>
      </template>
    </alert-dialog>

    <!-- Transfer dialog -->
    <transfer-dialog
      :is-open="isTransferDialogOpen"
      :workpackage-to-transfer="workpackageToTransfer"
      @onCancel="closeTransferDialog"
      @onOk="runTransferWorkpackage"
    />

    <!-- ExportPricing dialog -->
    <alert-dialog
      :is-open="isExportPricingDialogOpen"
      :is-max-height-fixed="true"
      :ok-btn-text="
        useSapExport
          ? $t(
              `workPackages.exportToSAP.${
                sapExportParams.toFTP ? 'exportToSystem' : 'downloadFile'
              }`
            )
          : $t('actions.downloadPricing')
      "
      @onOk="runExport"
      @onCancel="closeExportPricingDialog"
    >
      <template v-slot:header>
        {{
          useSapExport ? $t('workPackages.exportToSAP.title') : $t('dialogs.downloadPricing.header')
        }}
      </template>
      <template v-slot:body>
        <div v-if="!workpackageToExportPricing || exporting(workpackageToExportPricing)">
          {{ $t('general.loadingMessage') }}
        </div>
        <div v-else>
          <div v-if="noProductsGoLiveAfterWorkpackage">
            {{ $t('dialogs.downloadPricing.noProductsGoLiveAfterWP') }}
          </div>
          <div v-else>
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span class="panel-header">
                    {{ $t('dialogs.downloadPricing.hasProductsGoLiveAfterWP') }}
                  </span>
                  <template v-slot:actions>
                    <v-icon>expand_more</v-icon>
                  </template>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <p
                    v-for="(productKey, index) in sortedProductsGoLiveAfterWorkpackage"
                    :key="`productKey-${index}`"
                    class="mb-1"
                  >
                    {{ productKey }}
                  </p>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
          <br />

          <feature-toggle :toggle="useManualProductGoLiveDate">
            <div v-if="noProductsGoLiveBeforeWorkpackage">
              {{ $t('dialogs.downloadPricing.noProductsGoLiveBeforeWP') }}
            </div>
            <div v-else>
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span class="panel-header">
                      {{ $t('dialogs.downloadPricing.hasProductsGoLiveBeforeWP') }}
                    </span>
                    <template v-slot:actions>
                      <v-icon>expand_more</v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <p
                      v-for="(productKey, index) in sortedProductsGoLiveBeforeWorkpackage"
                      :key="`productKey-${index}`"
                      class="mb-1"
                    >
                      {{ productKey }}
                    </p>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
            <br />

            <div v-if="noProductsGoLiveBeforeToday">
              {{ $t('dialogs.downloadPricing.noProductsGoLiveBeforeToday') }}
            </div>
            <div v-else>
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span class="panel-header">
                      {{ $t('dialogs.downloadPricing.hasProductsGoLiveBeforeToday') }}
                    </span>
                    <template v-slot:actions>
                      <v-icon>expand_more</v-icon>
                    </template>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <p
                      v-for="(productKey, index) in sortedProductsGoLiveBeforeToday"
                      :key="`productKey-${index}`"
                      class="mb-1"
                    >
                      {{ productKey }}
                    </p>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </feature-toggle>
        </div>
      </template>
    </alert-dialog>

    <!-- Export API dialog -->
    <alert-dialog
      :is-open="isExportAPIDialogOpen"
      :ok-btn-text="$t('actions.postPricing')"
      @onOk="handleExportToAPI"
      @onCancel="closeExportAPIDialog"
    >
      <template v-slot:header>{{ $t('dialogs.exportAPI.header') }}</template>
      <template v-slot:body>
        <p id="export-api-warning">
          <strong>{{ $t('dialogs.exportAPI.warning') }}</strong>
        </p>
        {{ $t('dialogs.exportAPI.body') }}
        <strong>{{ workpackageToExportAPI ? workpackageToExportAPI.description : '' }}</strong>
      </template>
    </alert-dialog>

    <div class="work-packages-header d-flex align-center">
      <h2 class="panel-header flex-grow-1">{{ $t(heading) }}</h2>
      <v-btn icon @click="toggleWorkpackagesExpansion">
        <v-icon>{{ getExpansionIcon }}</v-icon>
      </v-btn>
    </div>
    <div v-show="workpackagesExpanded">
      <div v-if="!loading" class="work-packages-subheader mb-4 d-flex align-center">
        <div v-if="searchWorkpackages" class="search-wp-field-wrapper pr-4">
          <v-text-field
            class="search-wp-field search-field"
            :value="search"
            append-icon="search"
            :label="$t('actions.search')"
            single-line
            :loading="loading"
            @input="debounceSearchUpdate"
          />
        </div>
        <div class="add-workpackage-btn-wrapper pl-2">
          <v-btn
            v-if="canCreateWorkpackage"
            class="pa-0 new-workpackage-btn"
            text
            e2e-slug="btnCreateWPNew"
            @click="createNewWorkpackage"
          >
            <span class="icon-square">
              <v-icon small color="white">mdi-plus</v-icon>
            </span>
            <span class="btn-label">{{ $t('workPackages.createNew') }}</span>
          </v-btn>
        </div>
      </div>

      <div class="work-packages-list">
        <!-- Loading state -->
        <div v-if="loading" class="text-center my-4">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <!-- No data state -->
        <div v-else-if="allWorkpackages.length === 0" class="no-data-text">
          <span>{{ $t('workPackages.noData') }}</span>
        </div>

        <!-- Workpackage list -->
        <div v-else class="workpackage-items">
          <div
            v-for="workpackage in allWorkpackages"
            :key="workpackage._id"
            class="workpackage-item-wrapper"
          >
            <!-- Your custom workpackage-item component -->
            <workpackage-item
              :workpackage="workpackage"
              :is-selected="workpackage._id === selectedWorkpackage._id"
              :is-expanded="isExpanded(workpackage._id)"
              :max-unit-count="maxUnitCount"
              :allow-copy="allowCopy"
              :has-export-info="hasExportInfo"
              :get-export-last-text="getExportLastText"
              :column-formatters="columnFormatters"
              :fields="fields"
              @selectWorkpackage="selectWorkpackage"
              @toggle-expansion="toggleExpansion"
              @open-delete-dialog="openDeleteDialog"
              @open-transfer-dialog="openTransferDialog"
              @open-export-api-dialog="openExportAPIDialog"
              @export-pricing="openExportPricingDialog"
              @open-workpackage-copy-dialog="openWorkpackageCopyDialog"
              @trigger-download-export-to-sap="triggerDownloadExportToSAP"
            />
          </div>
        </div>
      </div>
    </div>
    <copy-workpackage-dialog
      v-if="isCreateWorkpackageFromCopyEnabled && allowCopy && canEditWorkpackage"
      :is-open="isCopyWorkpackageDialogOpen"
      :options="copyWorkpackageOptions"
      @onOk="confirmCopyWorkpackage"
      @onCancel="cancelCopyWorkpackage"
    />
    <create-workpackage-dialog
      :is-open.sync="showCreateDialog"
      @created="handleWorkpackageCreated"
      @cancel="showCreateDialog = false"
    />
  </v-container>
</template>

<script>
import to from 'await-to-js';
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import { max, isEmpty, sortBy, get, debounce, omit, find, findIndex, defaultTo } from 'lodash';
import moment from 'moment';
import dateHasPassed from '../../../store/utils/date-checking';
import workpackageTypes from '@enums/workpackage-types';
import {
  yearMonthDayFormat,
  utcFormat,
  hoursMinutesDayMonthYearSeparatedFormat,
} from '@enums/date-formats';
import {
  displayExportToSAPButton,
  displayExportToAPIButton,
  owUsersCanExport,
  showGoLiveDateDialog,
  useManualProductGoLiveDate,
  enableAOExtracts,
  usePeriodIdsInAOExtracts,
  workpackageCreationHierarchyLevel,
  createWorkpackageFromCopy,
} from '@enums/feature-flags';
import { pricingGroupLevel, categoryLevel } from '@enums/hierarchy';
import exportType from '@enums/export-type';
import sapExportUtilsMixin from '../../../mixins/sapExportUtils';
import featureFlagsMixin from '../../../mixins/featureFlags';
import clientConfig from '@sharedModules/config/client';
import Events from '@enums/events';
import axios from 'axios';
import { descending } from '@enums/sort-direction';
import numberFormats from '@enums/number-formats';

const defaultCopyWorkpackageOptions = () => ({
  source: {},
  target: {},
  reuseExistingAOExtracts: true,
  selectedAOExtracts: [],
});
const todaysDate = moment().format('YYYY-MM-DD');
const sixMonthsAgo = moment()
  .subtract(6, 'months')
  .format('YYYY-MM-DD');

export default {
  mixins: [sapExportUtilsMixin, featureFlagsMixin],
  props: {
    heading: {
      type: String,
      required: true,
    },
    filteredWorkpackages: {
      type: Array,
      required: true,
    },
    sortDesc: {
      type: Boolean,
      required: true,
    },
    allowNew: {
      type: Boolean,
      required: true,
    },
    allowCopy: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: true,
    },
    searchWorkpackages: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      search: '',
      workpackageTypes,
      showCreateDialog: false,
      goLiveDate: new Date().toISOString().substr(0, 10),
      completionDate: new Date().toISOString().substr(0, 10),
      referenceDate: null,
      todaysDate,
      sixMonthsAgo,
      newWorkpackage: {},
      isDeleteDialogOpen: false,
      isTransferDialogOpen: false,
      isExportPricingDialogOpen: false,
      isExportAPIDialogOpen: false,
      workpackageToDelete: undefined,
      workpackageToTransfer: {},
      workpackageToExportPricing: undefined,
      workpackageToExportAPI: undefined,
      workpackagesExpanded: this.expanded,
      sorting: {},
      enableAOExtracts,
      usePeriodIdsInAOExtracts,
      displayExportToSAPButton,
      displayExportToAPIButton,
      exportType,
      unitsToDisplay: 15,
      useManualProductGoLiveDate,
      useSapExport: false,
      sapExportParams: {},
      loadingPenaltyExports: false,
      selectedAOExtracts: [],
      pricingGroupLevel,
      categoryLevel,
      exportLastInfo: {}, // format: {workpackageId: {api: {...}, sap: {...}}}
      isExportLastInfoLoading: {}, // format: {workpackageId: {api: boolean, sap: boolean}}
      isCopyWorkpackageDialogOpen: false,
      copyWorkpackageOptions: defaultCopyWorkpackageOptions(),
    };
  },

  computed: {
    ...mapState('context', ['profile']),
    ...mapState('clientConfig', ['i18nconfig', 'exportConfigs', 'toggleLogic']),
    ...mapState('workpackages', [
      'loading',
      'inactiveLoading',
      'workpackages',
      'selectedWorkpackage',
      'creating',
      'deletingIds',
      'exportingIds',
      'postingIds',
      'exportingToSAPIds',
      'refreshingIds',
      'productsGoLiveBeforeToday',
      'productsGoLiveBeforeWorkpackage',
      'productsGoLiveAfterWorkpackage',
    ]),
    ...mapGetters('workpackages', [
      'masterWorkpackages',
      'activeWorkpackages',
      'archivedWorkpackages',
      'isAnyWorkpackageInTransferList',
      'getAvailableAOExtracts',
      'getAOExtractsByKey',
      'getAssociatedPeriodIds',
      'isAOExtractsValid',
    ]),
    ...mapGetters('context', [
      'isUnitManager',
      'isAdminLoggedIn',
      'isOWUser',
      'canExecuteWorkpackage',
      'canEditWorkpackage',
    ]),
    ...mapGetters('hierarchy', ['getHierarchyForWorkpackage']),

    allWorkpackages() {
      // First filter by search if needed
      let result = this.filteredWorkpackages;

      if (this.search) {
        const searchLower = this.search.toLowerCase();
        result = result.filter(workpackage =>
          get(workpackage, 'description', '')
            .toLowerCase()
            .includes(searchLower)
        );
      }

      // Then apply sorting based on this.sorting configuration
      return sortBy(result, [
        workpackage =>
          defaultTo(
            moment(get(workpackage, 'goLiveDate'), yearMonthDayFormat, true).valueOf(),
            this.sortDesc ? -Infinity : Infinity
          ) * (this.sortDesc ? -1 : 1),
        workpackage => get(workpackage, 'description', '').toLowerCase(),
      ]);
    },

    maxUnitCount() {
      // we're forcing minimum 4 units
      return max([
        4,
        ...this.workpackages.map(workpackage => {
          const retailUnits = get(workpackage, 'units', []).filter(
            unit => unit.wholesale === false
          );
          return max([retailUnits.length, 4]);
        }),
      ]);
    },

    fields() {
      const results = 'workpackage.results';
      return {
        storeFormat: this.$t(`${results}.storeFormat`),
        region: this.$t(`${results}.region`),
        productKeyDisplay: this.$t(`${results}.productKey`),
        date: this.$t(`${results}.date`),
        dateFormat: this.$t(`${results}.dateFormat`),
        price: this.$t(`${results}.price`),
      };
    },

    apiExportFields() {
      const results = 'workpackage.exports.api';
      return {
        storeFormat: this.$t(`${results}.storeFormat`),
        productKeyDisplay: this.$t(`${results}.productKey`),
        date: this.$t(`${results}.date`),
        dateFormat: this.$t(`${results}.dateFormat`),
        price: this.$t(`${results}.price`),
      };
    },

    exportToSAPFields() {
      return this.getSapExportFields();
    },

    getExpansionIcon() {
      return this.workpackagesExpanded ? 'expand_less' : 'expand_more';
    },

    inactiveWps() {
      return this.heading === 'home.archivedHeading';
    },

    disableOWUserExport() {
      return !this.isFeatureFlagEnabled(owUsersCanExport) && this.isOWUser;
    },

    noProductsGoLiveBeforeToday() {
      return isEmpty(this.productsGoLiveBeforeToday);
    },

    noProductsGoLiveBeforeWorkpackage() {
      return isEmpty(this.productsGoLiveBeforeWorkpackage);
    },

    noProductsGoLiveAfterWorkpackage() {
      return isEmpty(this.productsGoLiveAfterWorkpackage);
    },

    displayGoLiveDateDialog() {
      return !!this.toggleLogic[showGoLiveDateDialog];
    },

    isXSRClient() {
      return this.toggleLogic.workpackageExportFunction === 'exportXSRWorkpackage';
    },

    sortedProductsGoLiveBeforeToday() {
      return sortBy(this.productsGoLiveBeforeToday);
    },

    sortedProductsGoLiveBeforeWorkpackage() {
      return sortBy(this.productsGoLiveBeforeWorkpackage);
    },

    sortedProductsGoLiveAfterWorkpackage() {
      return sortBy(this.productsGoLiveAfterWorkpackage);
    },

    creationHierarchyLevel() {
      return this.toggleLogic[workpackageCreationHierarchyLevel];
    },

    canCreateWorkpackage() {
      return this.allowNew && !this.showCreateDialog && this.canEditWorkpackage;
    },

    aoExtractsOptions() {
      return this.getAvailableAOExtracts(this.goLiveDate);
    },

    sourceWorkpackages() {
      if (this.isCreateWorkpackageFromCopyEnabled) {
        return [
          ...this.masterWorkpackages,
          ...this.activeWorkpackages,
          ...this.archivedWorkpackages,
        ];
      }
      return this.masterWorkpackages;
    },

    isCreateWorkpackageFromCopyEnabled() {
      return this.isFeatureFlagEnabled(createWorkpackageFromCopy);
    },

    columnFormatters() {
      return {
        productKeyDisplay: {
          type: get(
            this.exportConfigs,
            'exportToExcel.columnFormatter.productKeyDisplay',
            numberFormats.integer
          ),
        },
      };
    },
  },

  created() {
    this.sorting = {
      sortBy: ['goLiveDate', 'description'],
      sortDesc: [this.sortDesc, false],
    };
  },

  methods: {
    ...mapActions('workpackages', [
      'setUserSelectionWorkpackage',
      'downloadWorkpackageResults',
      'exportWorkpackageResultsToAPI',
      'downloadExportToSAP',
      'downloadXSROutboundExport',
      'createWorkpackage',
      'deleteWorkpackage',
      'addWorkpackageToExportingList',
      'removeWorkpackageFromExportingList',
      'transferWorkpackage',
      'previewWorkpackageResults',
      'downloadPenaltyExportData',
    ]),
    ...mapActions('storeGroupRelationships', ['fetchStoregroupKeyNameMap']),
    ...mapMutations('workpackages', ['defaultCreateWorkpackageOptions']),

    debounceSearchUpdate: debounce(function(value) {
      this.search = value;
    }, clientConfig.inputDebounceValue),

    async exportPenaltyData({ workpackage }) {
      this.loadingPenaltyExports = true;
      const [err] = await to(this.downloadPenaltyExportData({ workpackage }));
      if (err) this.loadingPenaltyExports = false;
      this.loadingPenaltyExports = false;
    },

    triggerDownloadExportToSAP(event) {
      if (this.displayGoLiveDateDialog) {
        this.useSapExport = true;
        this.sapExportParams = event;
        const workpackage = event.selectedWorkpackage;
        this.openExportPricingDialog(workpackage);
      } else if (this.isXSRClient) {
        // Use XSR export for local download when isXSRClient is true
        this.downloadXSROutboundExport({
          selectedWorkpackage: event.selectedWorkpackage,
          exportConfig: event.exportConfig,
        });
      } else {
        this.downloadExportToSAP(event);
      }
    },

    async callDownloadExportToSAP() {
      this.isExportPricingDialogOpen = false;
      await this.downloadExportToSAP(this.sapExportParams);
      this.useSapExport = false;
      this.sapExportParams = {};

      await this.fetchExportToSAPLastExportInfo(this.workpackageToExportAPI);
    },

    handleScroll(event) {
      const isUserScrollingHorizontally = event.target.scrollLeft > 0;
      if (isUserScrollingHorizontally) {
        // The Magic Number 9 used below ensures that we never see blank units
        // ie the next units to be shown are loaded before we scroll there
        let percentageScrolled = event.target.scrollLeft / event.target.scrollWidth;
        // event.target.scrollLeft will never equal event.target.scrollWidth
        if (percentageScrolled > 0.9) percentageScrolled = 1;
        const newUnitsToDisplay = Math.round(this.maxUnitCount * percentageScrolled) + 9;
        this.unitsToDisplay =
          newUnitsToDisplay > this.unitsToDisplay - 9 ? newUnitsToDisplay : this.unitsToDisplay;
      }
    },

    deleting(workpackage) {
      return this.deletingIds.includes(workpackage._id);
    },

    transferring(workpackage) {
      return this.isAnyWorkpackageInTransferList([workpackage._id]);
    },

    exportingToSAP(workpackage) {
      return this.exportingToSAPIds.includes(workpackage._id);
    },

    exporting(workpackage) {
      return this.exportingIds.includes(workpackage._id);
    },

    posting(workpackage) {
      return this.postingIds.includes(workpackage._id);
    },

    refreshing(workpackage) {
      return this.refreshingIds.includes(workpackage._id);
    },

    disabled(workpackage) {
      return (
        this.exporting(workpackage) ||
        this.posting(workpackage) ||
        this.exportingToSAP(workpackage) ||
        this.refreshing(workpackage) ||
        this.deleting(workpackage) ||
        this.transferring(workpackage)
      );
    },

    openExportPricingDialog(workpackage) {
      this.workpackageToExportPricing = workpackage;
      const params = {
        selectedWorkpackage: workpackage,
        fields: this.fields,
        exportType: exportType.exportPricing,
      };
      this.previewWorkpackageResults(params);

      this.isExportPricingDialogOpen = true;
    },

    closeExportPricingDialog() {
      this.useSapExport = false;
      this.sapExportParams = {};
      this.isExportPricingDialogOpen = false;
    },

    async openExportAPIDialog(workpackage) {
      this.workpackageToExportAPI = workpackage;
      this.isExportAPIDialogOpen = true;
    },

    closeExportAPIDialog() {
      this.workpackageToExportAPI = undefined;
      this.isExportAPIDialogOpen = false;
    },

    runExportPricing() {
      this.isExportPricingDialogOpen = false;
      const params = {
        selectedWorkpackage: this.workpackageToExportPricing,
        fields: this.fields,
        exportType: exportType.exportPricing,
        columnFormatters: this.columnFormatters,
      };
      this.downloadWorkpackageResults(params);
    },

    runExport() {
      if (this.useSapExport) return this.callDownloadExportToSAP();
      return this.runExportPricing();
    },

    openDeleteDialog(workpackage) {
      this.workpackageToDelete = workpackage;
      this.isDeleteDialogOpen = true;
    },

    closeDeleteDialog() {
      this.isDeleteDialogOpen = false;
      this.workpackageToDelete = undefined;
    },

    openTransferDialog(workpackage) {
      this.workpackageToTransfer = workpackage;
      this.isTransferDialogOpen = true;
    },

    closeTransferDialog() {
      this.isTransferDialogOpen = false;
      this.workpackageToTransfer = {};
    },

    async runTransferWorkpackage(event) {
      const { selectedFields, destinationWorkpackage, selectedCategories } = event;
      this.isTransferDialogOpen = false;
      this.transferWorkpackage({
        destinationWorkpackageId: destinationWorkpackage._id,
        sourceWorkpackageId: this.workpackageToTransfer._id,
        fields: selectedFields,
        selectedCategories,
      });
      this.workpackageToTransfer = {};
    },

    async runDeleteWorkpackage() {
      this.isDeleteDialogOpen = false;
      await this.deleteWorkpackage(this.workpackageToDelete);
    },

    isSelected(workpackage) {
      return workpackage._id === this.selectedWorkpackage._id ? 'selected' : '';
    },

    isActive(date, mocked) {
      return dateHasPassed(date) || mocked ? 'archived' : '';
    },

    isExpanded(workpackageId) {
      const workpackage = find(this.allWorkpackages, { _id: workpackageId });
      return workpackage && workpackage.isExpanded === true;
    },

    toggleExpansion(workpackageId) {
      const workpackage = find(this.allWorkpackages, { _id: workpackageId });
      if (!workpackage || get(workpackage, 'mocked')) return;

      const wasExpanded = workpackage.isExpanded;
      workpackage.isExpanded =
        workpackage.isExpanded === undefined ? true : !workpackage.isExpanded;

      const index = findIndex(this.allWorkpackages, { _id: workpackageId });
      this.$set(this.allWorkpackages, index, workpackage);

      if (workpackage.isExpanded) {
        this.fetchStoregroupKeyNameMap(workpackageId);
      }

      if (workpackage.isExpanded && !wasExpanded) {
        this.fetchExportToAPILastExportInfo(workpackage);
        this.fetchExportToSAPLastExportInfo(workpackage);
      }
    },

    toggleWorkpackagesExpansion() {
      this.workpackagesExpanded = !this.workpackagesExpanded;
      if (this.workpackagesExpanded) this.$emit('expand');
    },

    async selectWorkpackage(workpackage) {
      if (get(workpackage, 'mocked')) return;
      const workpackageId = workpackage._id;
      await this.setUserSelectionWorkpackage({ selectedWorkpackageId: workpackageId });
      if (this.isUnitManager && !this.isAdminLoggedIn) {
        this.$router.push({ path: `pricing/impact-view/workpackage/${workpackageId}` });
      } else {
        this.$router.push({ path: `pricing/desk-view/workpackage/${workpackageId}` });
      }
    },

    createNewWorkpackage() {
      this.showCreateDialog = true;
    },

    handleWorkpackageCreated() {
      this.showCreateDialog = false;
    },

    async handleExportToAPI() {
      await this.exportWorkpackageResultsToAPI({
        selectedWorkpackage: this.workpackageToExportAPI,
        apiExportFields: this.apiExportFields,
      });

      await this.fetchExportToAPILastExportInfo(this.workpackageToExportAPI);

      this.closeExportAPIDialog();
    },

    async fetchExportLastInfo(workpackage, type) {
      if (!workpackage || !workpackage._id) return;

      const workpackageId = workpackage._id;
      const exportTypeKey = type === exportType.exportToSAP ? 'sap' : 'api';

      if (!this.isExportLastInfoLoading[workpackageId]) {
        this.$set(this.isExportLastInfoLoading, workpackageId, {});
      }
      if (!this.exportLastInfo[workpackageId]) {
        this.$set(this.exportLastInfo, workpackageId, {});
      }

      this.$set(this.isExportLastInfoLoading[workpackageId], exportTypeKey, true);

      try {
        const whereClause = {
          fullEventName: Events.workpackage.exportResults,
          entityIds: { $in: [workpackageId] },
          'extra.responseStatusCode': 200,
          'extra.queryParams.exportType': { $exists: false },
        };

        if (type === exportType.exportToSAP) {
          whereClause['extra.queryParams.exportType'] = String(exportType.exportToSAP);
          whereClause['extra.toFTP'] = true;
        }

        const params = {
          where: whereClause,
          sortBy: 'date',
          sortDirection: descending,
          limit: 1,
        };

        const { data: auditLogs } = await axios.get('/api/audit-log', { params });
        const result = !isEmpty(auditLogs) ? auditLogs[0] : null;

        // Store result
        this.$set(this.exportLastInfo[workpackageId], exportTypeKey, result);
      } catch (error) {
        console.error(`Error fetching last ${exportTypeKey} export info:`, error);
        this.$set(this.exportLastInfo[workpackageId], exportTypeKey, null);
      } finally {
        this.$set(this.isExportLastInfoLoading[workpackageId], exportTypeKey, false);
      }
    },

    async fetchExportToAPILastExportInfo(workpackage) {
      if (!workpackage || !workpackage._id) return;
      await this.fetchExportLastInfo(workpackage);
    },

    async fetchExportToSAPLastExportInfo(workpackage) {
      if (!workpackage || !workpackage._id) return;
      await this.fetchExportLastInfo(workpackage, exportType.exportToSAP);
    },

    getExportLastText(workpackageId, type) {
      const exportTypeKey = type === exportType.exportToSAP ? 'sap' : 'api';
      const info = get(this.exportLastInfo, [workpackageId, exportTypeKey]);
      if (!info) return '';

      return this.$t('workPackages.lastExport', {
        date: moment(info.date).format(hoursMinutesDayMonthYearSeparatedFormat),
        username: info.username,
      });
    },

    hasExportInfo(workpackageId, type) {
      const exportTypeKey = type === exportType.exportToSAP ? 'sap' : 'api';
      return !!get(this.exportLastInfo, [workpackageId, exportTypeKey]);
    },

    openWorkpackageCopyDialog(workpackage) {
      this.defaultCreateWorkpackageOptions();
      this.isCopyWorkpackageDialogOpen = true;
      this.copyWorkpackageOptions = {
        reuseExistingAOExtracts: true,
        source: workpackage,
        target: {
          workpackageId: workpackage._id,
          description: this.$t('workPackages.copyOf', { workpackageName: workpackage.description }),
          storeFormat: workpackage.storeFormat,
          completionDate: moment(workpackage.completionDate).format(yearMonthDayFormat),
          goLiveDate: workpackage.goLiveDate,
        },
        selectedAOExtracts: workpackage.aoExtracts
          ? workpackage.aoExtracts.map(extract => extract.auditCorrelationId)
          : [],
      };
      if (workpackage.referenceDate) {
        this.copyWorkpackageOptions.target.referenceDate = moment(workpackage.referenceDate).format(
          yearMonthDayFormat
        );
      }
    },

    async confirmCopyWorkpackage() {
      this.isCopyWorkpackageDialogOpen = false;

      // copy target to avoid datepicker Error in render: "RangeError: Invalid time value" in copyWorkpackageDialog
      const newWorkpackage = { ...this.copyWorkpackageOptions.target };

      newWorkpackage.isCopy = true;
      newWorkpackage.owner = this.profile.username;
      newWorkpackage.hierarchyLevel = this.creationHierarchyLevel;

      newWorkpackage.hierarchyIds = this.getHierarchyForWorkpackage(
        {
          level: this.creationHierarchyLevel,
        },
        this.copyWorkpackageOptions.source._id
      ).map(hierarchy => hierarchy.levelEntryKey);

      newWorkpackage.completionDate = moment(newWorkpackage.completionDate).format(utcFormat);

      // YYYY-MM-DD format is required for analytics
      newWorkpackage.goLiveDate = moment(newWorkpackage.goLiveDate).format(yearMonthDayFormat);
      if (newWorkpackage.referenceDate) {
        newWorkpackage.referenceDate = moment(newWorkpackage.referenceDate).format(
          yearMonthDayFormat
        );
      }

      if (this.isFeatureFlagEnabled(enableAOExtracts)) {
        if (this.copyWorkpackageOptions.reuseExistingAOExtracts) {
          if (!isEmpty(this.copyWorkpackageOptions.source.aoExtracts)) {
            // reuse existing AO extracts
            // filter existing extracts by new target workpackage go live date
            const newAvailableAOExtracts = this.getAvailableAOExtracts(newWorkpackage.goLiveDate);
            const filteredAOExtracts = this.copyWorkpackageOptions.source.aoExtracts.filter(
              aoExtract =>
                newAvailableAOExtracts.some(
                  e => e.auditCorrelationId === aoExtract.auditCorrelationId
                )
            );
            newWorkpackage.aoExtracts = filteredAOExtracts;
          }
        } else {
          // user selected new AO extracts manually
          newWorkpackage.aoExtracts = this.copyWorkpackageOptions.selectedAOExtracts.map(id =>
            omit(this.getAOExtractsByKey[id], 'displayName')
          );
        }
      }

      await this.createWorkpackage(newWorkpackage);

      this.isCopyWorkpackageDialogOpen = false;
      this.copyWorkpackageOptions = defaultCopyWorkpackageOptions();
    },

    cancelCopyWorkpackage() {
      this.isCopyWorkpackageDialogOpen = false;
      this.copyWorkpackageOptions = defaultCopyWorkpackageOptions();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.work-packages-header {
  h2 {
    font-size: 1.6rem;
    font-weight: 500;
    color: #343a3f;
  }
}

.add-workpackage-btn-wrapper {
  border-left: 1px solid #a2a9b0;
}

.new-workpackage-btn {
  background: none !important;
  box-shadow: none !important;
  border: none !important;
  min-width: 0;
  padding: 0 8px;
  font-weight: 400;

  .icon-square {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #023b6e;
    border-radius: 4px;
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }

  .btn-label {
    color: #023b6e;
    font-size: 1.4rem;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 3.2rem;
    vertical-align: middle;
  }

  &:hover {
    background: none !important;
    box-shadow: none !important;
  }
}

.search-wp-field {
  width: 220px;
  min-width: 220px;
  max-width: 220px;
  padding: 0;
  margin: 0;

  .v-input__control {
    .v-input__slot {
      padding: 0 8px;
      margin: 0;
      background: transparent !important;
    }
  }
}

#export-api-warning {
  color: $pricing-red;
}

.panel-header {
  font-size: 1.4rem;
}

.work-packages-container {
  &:first-child {
    padding-top: 0 !important;
  }

  &:not(:first-child) {
    border-top: 1px solid $pricing-grey-border;
  }
}
</style>
